import * as React from 'react'
import { useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { Loader } from 'components/loader'
import { UsersTab } from '../users-tab'
import { useStores } from 'utils/hooks/useStores'
import style from '../user.css'

interface UserSearchResultProps {
  handleEditUser(user: any): void
  isUserItemLoading?: boolean
  handleReactivateUser?(userId: string): Promise<void>
  handleResetPassword?(user: any): void
  handleUnlockUser?(userId: string): Promise<void>
  handleCloneUser?(user: any): void
  handleUserInfo?(user: any): void
}

const UserSearchResultComponent = observer((props: UserSearchResultProps) => {
  const { t } = useTranslation()
  const appStore = useStores()
  const usersStore = appStore.usersStore

  const {
    handleEditUser,
    handleReactivateUser,
    handleResetPassword,
    handleUnlockUser,
    handleCloneUser,
    handleUserInfo,
  } = props

  const searchData = useMemo(() => {
    const {
      isUserItemLoading,
      invitedSearchedUsers,
      activeSearchedUsers,
      suspendedSearchedUsers,
      deactivatedSearchedUsers,
      lockedOutSearchUsers,
      expiredPasswordSearchUsers,
      expiredPasswordUsers,
      lockedUsers,
    } = usersStore

    return {
      isUserItemLoading,
      sections: [
        {
          key: 'active',
          title: t('users.active').toUpperCase(),
          users: activeSearchedUsers,
          type: 'active' as const,
          handlers: { handleEditUser, handleResetPassword, handleCloneUser, handleUserInfo },
          countMessage: t('users.searchMatchActive', { count: activeSearchedUsers.length }),
        },
        {
          key: 'invited',
          title: t('users.invited').toUpperCase(),
          users: invitedSearchedUsers,
          type: 'invited' as const,
          handlers: { handleEditUser, handleReactivateUser, handleResetPassword, handleUserInfo },
          countMessage: t('users.searchMatchInvited', { count: invitedSearchedUsers.length }),
          showLoader: isUserItemLoading,
        },
        {
          key: 'suspended',
          title: t('users.suspended').toUpperCase(),
          users: suspendedSearchedUsers,
          type: 'suspended' as const,
          handlers: { handleEditUser, handleUserInfo },
          countMessage: t('users.searchMatchSuspended', { count: suspendedSearchedUsers.length }),
        },
        {
          key: 'deactivated',
          title: t('users.deactivated').toUpperCase(),
          users: deactivatedSearchedUsers,
          type: 'deactivated' as const,
          handlers: { handleEditUser, handleUserInfo },
          countMessage: t('users.searchMatchDeactivated', {
            count: deactivatedSearchedUsers.length,
          }),
          conditional: deactivatedSearchedUsers.length > 0,
        },
        {
          key: 'locked',
          title: t('users.locked').toUpperCase(),
          users: lockedOutSearchUsers,
          type: 'locked' as const,
          handlers: { handleEditUser, handleUnlockUser, handleResetPassword, handleUserInfo },
          countMessage: t('users.searchMatchLocked', { count: lockedOutSearchUsers.length }),
          conditional: lockedOutSearchUsers.length > 0,
          showTitleWhenEmpty: lockedUsers.length > 0 && lockedOutSearchUsers.length === 0,
          showCountWhenEmpty: lockedUsers.length > 0,
        },
        {
          key: 'expired',
          title: t('users.expired').toUpperCase(),
          users: expiredPasswordSearchUsers,
          type: 'expired' as const,
          handlers: { handleEditUser, handleResetPassword, handleUserInfo },
          countMessage: t('users.searchMatchExpired', { count: expiredPasswordSearchUsers.length }),
          showCountWhenEmpty: expiredPasswordUsers.length > 0,
        },
      ],
    }
  }, [
    usersStore,
    t,
    handleEditUser,
    handleReactivateUser,
    handleResetPassword,
    handleUnlockUser,
    handleCloneUser,
    handleUserInfo,
  ])

  const renderUserSection = (section: (typeof searchData.sections)[0]) => {
    const {
      key,
      title,
      users,
      type,
      handlers,
      countMessage,
      conditional = true,
      showLoader = false,
      showTitleWhenEmpty = false,
      showCountWhenEmpty = false,
    } = section

    if (!conditional && !showTitleWhenEmpty && !showCountWhenEmpty) {
      return null
    }

    return (
      <React.Fragment key={key}>
        <div className={style.userSectionGap} />

        {(conditional || showTitleWhenEmpty) && (
          <div className={classNames(style.usersTypeSubTitle, style.zeroResultsSubtitle)}>
            {title}
          </div>
        )}

        {users.length > 0 && (
          <div className={style.userListWrapper}>
            {showLoader && <Loader fit />}
            <UsersTab users={users} type={type} {...handlers} />
          </div>
        )}

        {(conditional || showCountWhenEmpty) && (
          <div className={style.zeroResultsMsg}>{countMessage}</div>
        )}
      </React.Fragment>
    )
  }

  return <div>{searchData.sections.map(renderUserSection)}</div>
})

export const UserSearchResult = UserSearchResultComponent
